import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { Alert, Box, CircularProgress, Paper } from "@mui/material";
import { Document, Page, pdfjs } from "react-pdf";
import { useAuth0 } from "@auth0/auth0-react";
import urljoin from "url-join";
import { API_BASEPATH_PORTAL } from "@/config/env.config";
import { useIsAuthenticated } from "@/hooks/useIsAuthenticated";
import ManagementPageCard, { defaultInnerPanelSx } from "@/components/layout/ManagementPageCard.jsx";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const DOCUMENT_CONFIG = {
	eula: {
		title: "End User License Agreement",
		endpointParts: ["eula", "download"],
	},
	"fair-use-policy": {
		title: "Fair Use Policy",
		endpointParts: ["shared-files", "FairUsePolicy", "download"],
	},
};

export const LegalDocumentViewer = () => {
	const { documentType } = useParams();
	const history = useHistory();
	const location = useLocation();
	const isAuthenticated = useIsAuthenticated();
	const { isLoading: auth0IsLoading, getAccessTokenSilently } = useAuth0();

	const [documentUrl, setDocumentUrl] = useState("");
	const [error, setError] = useState("");
	const [isLoadingDocument, setIsLoadingDocument] = useState(false);
	const [numPages, setNumPages] = useState(0);
	const [pdfWidth, setPdfWidth] = useState(900);
	const documentPanelRef = useRef(null);

	const docConfig = useMemo(() => DOCUMENT_CONFIG[documentType], [documentType]);

	const fetchDocument = useCallback(async () => {
		if (!docConfig) {
			return;
		}

		try {
			setError("");
			setIsLoadingDocument(true);
			const token = await getAccessTokenSilently({ audience: "https://portal-api" });
			const url = urljoin(API_BASEPATH_PORTAL, ...docConfig.endpointParts);
			const response = await fetch(url, {
				method: "GET",
				headers: {
					Authorization: `Bearer ${token}`,
				},
			});

			if (!response.ok) {
				throw new Error(`Failed to load ${docConfig.title}: ${response.statusText}`);
			}

			const blob = await response.blob();
			const blobUrl = window.URL.createObjectURL(blob);
			setDocumentUrl((prevUrl) => {
				if (prevUrl) {
					window.URL.revokeObjectURL(prevUrl);
				}
				return blobUrl;
			});
		} catch (fetchError) {
			setError(fetchError?.message || "Unable to load document.");
		} finally {
			setIsLoadingDocument(false);
		}
	}, [docConfig, getAccessTokenSilently]);

	useEffect(() => {
		if (auth0IsLoading) {
			return;
		}

		if (!isAuthenticated) {
			const returnUrl = encodeURIComponent(`${location.pathname}${location.search}`);
			history.replace(`/login?return_url=${returnUrl}`);
			return;
		}

		fetchDocument();
	}, [auth0IsLoading, isAuthenticated, location.pathname, location.search, history, fetchDocument]);

	useEffect(() => {
		const updatePdfWidth = () => {
			const measuredWidth = documentPanelRef.current?.clientWidth || 0;
			if (!measuredWidth) {
				return;
			}
			setPdfWidth(Math.max(320, measuredWidth - 24));
		};

		updatePdfWidth();

		if (!documentPanelRef.current) {
			return;
		}

		const resizeObserver = new ResizeObserver(updatePdfWidth);
		resizeObserver.observe(documentPanelRef.current);
		return () => resizeObserver.disconnect();
	}, []);

	useEffect(() => {
		return () => {
			if (documentUrl) {
				window.URL.revokeObjectURL(documentUrl);
			}
		};
	}, [documentUrl]);

	if (!docConfig) {
		return (
			<ManagementPageCard
				title="Legal Document"
				subtitle="View legal documents associated with your Portal access."
			>
				<Alert severity="error">Unknown legal document.</Alert>
			</ManagementPageCard>
		);
	}

	return (
		<ManagementPageCard
			title={docConfig.title}
			subtitle="View legal documents associated with your Portal access."
		>
			<Box ref={documentPanelRef} sx={defaultInnerPanelSx}>
				<Paper variant="outlined" sx={{ width: "100%" }}>
					{auth0IsLoading || isLoadingDocument ? (
						<Box sx={{ p: 6, display: "flex", justifyContent: "center" }}>
							<CircularProgress size={28} />
						</Box>
					) : null}
					{error ? (
						<Box sx={{ p: 2 }}>
							<Alert severity="error">{error}</Alert>
						</Box>
					) : null}
					{!error && !auth0IsLoading && !isLoadingDocument && documentUrl ? (
						<Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
							<Document
								file={documentUrl}
								onLoadSuccess={({ numPages: loadedPages }) => setNumPages(loadedPages)}
								onLoadError={() => setError("Failed to render document.")}
								loading={<CircularProgress size={24} />}
							>
								{Array.from(new Array(numPages), (_, index) => (
									<Page
										key={`legal-page-${index + 1}`}
										pageNumber={index + 1}
										width={pdfWidth}
										renderAnnotationLayer={false}
										renderTextLayer={false}
									/>
								))}
							</Document>
						</Box>
					) : null}
				</Paper>
			</Box>
		</ManagementPageCard>
	);
};

export default LegalDocumentViewer;
